<template>
  <div>
    <ResourceLoadStatus
      v-if="loading || notFound || notAuthorized"
      :loading="loading"
      :not-found="notFound"
      :unauthorized="notAuthorized"
    />
    <div v-else-if="isMobileScreenSize === false" class="uk-flex uk-flex-center">
      <div
        class="uk-card uk-card-body uk-card-default invitation-container uk-width-5-6@s uk-width-1-2@m width-1-3@l uk-width-2-5@xl uk-text-center"
        v-if="invitationType && invitationType === 'Document'"
      >
        <img
          class="login-icon uk-padding uk-flex"
          style="width: 100%; object-fit: contain"
          src="/icons/Official-OpenVetting-Logo-Light.png"
        />
        <h2>{{ welcomeMsg }}</h2>
        <p class="uk-margin-large" v-html="details" />
        <p v-if="showTooLate">
          Unfortunately, someone else accepted the invitation to debate before you did
        </p>
        <p v-if="showTooLate">
          While you are here, on the OpenVetting forum, please continue on to MyDiscussions to
          contribute you own thoughts and make your case elsewhere.
        </p>
        <div v-if="permissionLevel === PermissionLevel.TEAM_LEAD && !showTooLate">
          <div v-if="requestedPrivacyLevel === 'Optional'">
            <p>
              {{ invitedBy.firstname }} has deferred to your choice whether this be kept
              confidential to yourselves respective advisors.
            </p>
            <div class="privacy-options">
              <form-group v-for="option in privacyOptions" :key="option.value">
                <label>
                  <input
                    class="uk-radio"
                    type="radio"
                    v-model="privacySelection"
                    :value="option.value"
                  />
                  <span class="uk-margin-small-left" v-html="option.label"></span>
                </label>
              </form-group>
            </div>
          </div>
          <p v-else-if="requestedPrivacyLevel === 'Private'">
            {{ invitedBy.firstname }} has asked this discussion be kept confidential to yourselves
            and respective advisors.
          </p>
          <p v-else class="uk-text-small">
            In the interest of public learning, discussions on OpenVetting are shareable via social
            media.
          </p>
        </div>
        <div v-if="permissionLevel === PermissionLevel.COHORT">
          <p v-if="requestedPrivacyLevel === 'Private'">
            {{ invitedBy.firstname }} has asked this discussion be kept confidential.
          </p>
        </div>
        <div v-if="!showTooLate" class="uk-flex uk-flex-around">
          <LoaderButton
            class="uk-width-1-3 uk-margin-large-top uk-text-bold uk-text-uppercase uk-flex uk-flex-center"
            :loaderStatus="declineStatus"
            @click="doDeclineClick"
            @keypress.enter="doDeclineClick"
            type="default"
          >
            Decline
          </LoaderButton>
          <LoaderButton
            class="uk-width-1-3 uk-margin-large-top uk-text-bold uk-text-uppercase uk-flex uk-flex-center"
            :loaderStatus="savingStatus"
            @click="doAcceptClick"
            @keypress.enter="doAcceptClick"
          >
            Accept
          </LoaderButton>
        </div>
      </div>
      <div class="uk-card uk-card-body uk-card-default uk-width-1-2 uk-text-center" v-else>
        <i uk-icon="icon: users; ratio: 8"></i>
        <h2>{{ welcomeMsg }}</h2>
        <p class="uk-margin-large">
          {{ details }}
        </p>
        <LoaderButton
          class="uk-width-1-1"
          :loaderStatus="savingStatus"
          @click="doAcceptClick"
          @keypress.enter="doAcceptClick"
        >
          Accept
        </LoaderButton>
        <LoaderButton class="uk-width-1-1" :loaderStatus="savingStatus" @click="doRejectClick">
          Reject
        </LoaderButton>
      </div>
    </div>
    <template v-else>
      <MobileLandingPage
        :joinDebate="true"
        :inviteBy="invitedBy"
        :invitations="invitations"
        :isAccept="isAccept"
        :isDecline="isDecline"
        :joinDebatePage="joinDebatePage"
        :declineStatus="declineStatus"
        :savingStatus="savingStatus"
        :document="document"
        @doAcceptClick="doAcceptClick()"
        @doDeclineClick="doDeclineClick()"
        @doReminderClick="doReminderClick"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState, mapActions } from 'vuex';
import PermissionLevel from '@/assets/js/enum/PermissionLevel';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import LoaderButton from '@/components/display/button/LoaderButton.vue';
import ResourceLoadStatus from '@/components/general/ResourceLoadStatus';
import { xhr } from '@/xhr/endpoints';
import { errors } from '@/errors/errors';
import MobileLandingPage from '@/views/public/MobileLandingPage';
import { EventBus } from '@/assets/js/event-bus';

export default {
  name: 'InvitationLanding',
  components: {
    LoaderButton,
    ResourceLoadStatus,
    MobileLandingPage,
  },
  data() {
    return {
      invitation: null,
      invitee: null,

      /** Type of invitation: Document, Organization */
      invitationType: null,
      isAccept: false,
      isDecline: false,
      joinDebatePage: true,
      loading: false,
      notAuthorized: false,
      notFound: false,
      showTooLate: false,
      savingStatus: '',
      declineStatus: '',
      privacySelection: 'Public',

      privacyOptions: [
        {
          label: 'Enable sharing',
          value: 'Public',
        },
        {
          label: 'Prefer confidentiality',
          value: 'Private',
        },
      ],
    };
  },

  computed: {
    PermissionLevel() {
      return PermissionLevel;
    },
    EventBus() {
      return EventBus;
    },
    ...mapState(['userData', 'idToken']),

    ...mapGetters(['isMobileScreenSize']),

    invitations() {
      return this.invitation ? this.invitation : {};
    },
    invitedBy() {
      return this.invitation && this.invitation?.invitedBy ? this.invitation.invitedBy : {};
    },
    organization() {
      return this.invitation && this.invitation?.organization?.name
        ? this.invitation.organization.name
        : '-';
    },
    document() {
      return this.invitation && this.invitation?.document ? this.invitation.document : {};
    },
    discourseRole() {
      return this.invitation && this.invitation?.discourseRole
        ? this.invitation.discourseRole
        : '-';
    },

    permissionLevel() {
      if (!this.invitation) return '';
      return this.invitation.permissionLevel === PermissionLevel.TEAM_LEAD &&
        this.invitation.invitees.find(invitee => invitee.status === 'Accepted')
        ? PermissionLevel.COHORT
        : this.invitation.permissionLevel;
    },

    requestedPrivacyLevel() {
      let privacy = this.invitation?.privacy;
      if (['Public', 'Private', 'Optional'].includes(privacy)) return privacy;
      let rubyHashString = privacy;
      let jsonString = '';
      if (rubyHashString) {
        jsonString = rubyHashString.replace(/:(\w+)=>/g, '"$1":').replace(/"/g, '"');
      }
      let jsonObject = '';
      if (jsonString) {
        jsonObject = JSON.parse(jsonString);
      }
      return this.invitation ? jsonObject.selected : '';
    },

    welcomeMsg() {
      if (this.showTooLate) {
        return 'The invitation to debate was accepted by someone else';
      } else if (this.invitationType === 'Document') {
        if (
          this.permissionLevel === PermissionLevel.TEAM_LEAD ||
          this.permissionLevel === PermissionLevel.COHORT
        ) {
          return `You've been Invited to Debate`;
        }
        return `You've been Invited to Collaborate`;
      }
      return `You've been Invited to an Organization`;
    },

    details() {
      if (this.permissionLevel === PermissionLevel.TEAM_LEAD) {
        if (this.showTooLate) {
          return `Thank you for your interest ${this.document.title} with ${this.invitedBy.firstname}`;
        } else if (this.invitation.invitees.find(invitee => invitee.status === 'Accepted')) {
          return `Thank you for your interest in "${this.document.title}." Unfortunately another invitee has already accepted.`;
        } else if (this.document.initialStance === 'Host') {
          return `You were invited by ${this.invitedBy.firstname + ' ' + this.invitedBy.lastname + ' ' + (this.invitedBy.myAffiliation ? ` from ${this.invitedBy.myAffiliation}` : '')} to substantiate your position regarding ${this.document.title}.`;
        } else if (this.document.initialStance === 'Opposition') {
          return `You were invited by ${this.invitedBy.firstname + ' ' + this.invitedBy.lastname + ' ' + (this.invitedBy.myAffiliation ? ` from ${this.invitedBy.myAffiliation}` : '')} to critique their position regarding ${this.document.title}.`;
        }
      } else if (this.permissionLevel === PermissionLevel.TEAM_MEMBER) {
        if (this.document.initialStance === 'Host') {
          return `You were invited by ${this.invitedBy.firstname + ' ' + this.invitedBy.lastname + ' ' + (this.invitedBy.myAffiliation ? ` from ${this.invitedBy.myAffiliation}` : '')} to help support their critique in ${this.document.title}.`;
        } else if (this.document.initialStance === 'Opposition') {
          return `You were invited by ${this.invitedBy.firstname + ' ' + this.invitedBy.lastname + ' ' + (this.invitedBy.myAffiliation ? ` from ${this.invitedBy.myAffiliation}` : '')} to help support their defense in ${this.document.title}.`;
        }
      } else if (this.permissionLevel === PermissionLevel.COHORT) {
        return `You were invited by ${this.invitedBy.firstname + ' ' + this.invitedBy.lastname + ' ' + (this.invitedBy.myAffiliation ? ` from ${this.invitedBy.myAffiliation}` : '')} to help support their  ${this.document.title}.`;
      }
      return `You were invited by ${this.invitedBy.firstname + ' ' + this.invitedBy.lastname} to join the "${this.organization}" organization`;
    },
  },

  async mounted() {
    //
    // start loader while retrieving invitation data
    this.loading = true;

    try {
      //
      // retrieve invitation data
      const response = await xhr.getInvitation(this.$route.params.invitationID);
      this.invitation = response.invitation;
      this.invitee = response.invitee;
      this.invitationType = response.type;
      //
      // setup privacy selection
      if (this.requestedPrivacyLevel !== 'Optional')
        this.privacySelection = this.requestedPrivacyLevel;
      //
      // validate invitees authorization
      // use case-insensitive validation
      const userEmailsLowerCase = this.userData.emails.map(email => email.toLowerCase());
      const inviteeData = this.invitation.invitees.find(invitee =>
        userEmailsLowerCase.includes(invitee.email.toLowerCase())
      );
      if (inviteeData.status === 'Expired') {
        this.showTooLate = true;
      }
      if (!inviteeData || inviteeData.status === 'Awaiting Send') this.notAuthorized = true;
    } catch (e) {
      if (e instanceof errors.UnauthorizedError) {
        this.notAuthorized = true;
      } else if (e instanceof errors.NotFoundError) {
        this.notFound = true;
      }
    } finally {
      //
      // stop loader
      this.loading = false;
    }
    if (!this.isMobileScreenSize) {
      let notifications = await xhr.getNotifications();
      this.setNotifications(notifications);
      EventBus.emit('set.notification.count', notifications);
    }
  },

  methods: {
    ...mapActions(['getNotifications']),
    ...mapMutations(['setUserData', 'setNotifications']),

    async doAcceptClick() {
      this.savingStatus = LoadingStatus.LOADING;
      switch (this.invitationType) {
        case 'Organization': {
          try {
            await xhr.acceptOrgInvitation(this.invitation._id);
            this.savingStatus = LoadingStatus.SUCCESS;
            this.$router.push(`/discussions`);
          } catch (e) {
            this.savingStatus = LoadingStatus.FAILED;
            console.log(e);
            alert('Could not accept Organization Debate invitation');
          }
          break;
        }
        case 'Document':
        default: {
          try {
            this.savingStatus = LoadingStatus.LOADING;
            await xhr.acceptTeamInvitation(this.invitation._id, this.privacySelection);
            this.savingStatus = LoadingStatus.SUCCESS;
            if (this.isMobileScreenSize) {
              this.joinDebatePage = false;
              this.isAccept = true;
            } else {
              if (this.permissionLevel === PermissionLevel.COHORT) {
                this.$router.pushRoute.CuratorDebate(this.invitation.document._id);
              } else {
                this.$router.pushRoute.Debate(this.invitation.document._id);
              }
            }
          } catch (e) {
            this.savingStatus = LoadingStatus.FAILED;
            console.log(e);
            this.showTooLate = true;
          }
          break;
        }
      }
    },
    async doDeclineClick() {
      try {
        this.declineStatus = LoadingStatus.LOADING;
        await xhr.declineTeamInvitation(this.invitee._id, this.idToken);
        this.declineStatus = LoadingStatus.SUCCESS;
        if (this.isMobileScreenSize) {
          this.joinDebatePage = false;
          this.isDecline = true;
        } else this.$router.push(`/inbox`);
      } catch (e) {
        this.declineStatus = LoadingStatus.FAILED;
        console.log(e);
        alert('Could not Decline Personal Debate invitation');
      }
    },
    async doReminderClick(time) {
      try {
        await xhr.reminderTeamInvitation(this.invitation._id, time, this.idToken);
      } catch (e) {
        console.log(e);
        alert('Could not Reminder Invitation');
      }
    },
    async doRejectClick() {
      this.savingStatus = LoadingStatus.LOADING;
      if (this.invitationType === 'Document') {
        this.savingStatus = LoadingStatus.SUCCESS;
      } else {
        try {
          await xhr.rejectOrgInvitation(this.invitation._id, this.idToken);
          this.savingStatus = LoadingStatus.SUCCESS;
          this.$router.push(`/inbox`);
        } catch (e) {
          this.savingStatus = LoadingStatus.FAILED;
          console.log(e);
          alert('Could not reject invitation');
        }
      }
    },
  },
};
</script>

<style lang="sass">
.error
  input
    border-color: red

.invitation-container
  border-radius: 15px

.privacy-options
  width: fit-content
  text-align: left
  margin: auto
</style>
